import { getApiConfig } from "@/config/api.config";
import { BaseApiService } from "@/services/api/BaseApiService";
import { CoreService } from "@/services/core/CoreService";

export interface Customer {
  id: number;
  name: string;
  initial: string | null;
  saveForLaterTemplate: string;
  live: boolean;
  active: boolean;
}

export interface CustomerNote {
  id: number;
  customerId: number;
  title: string;
  content: string;
  fileUrl?: string;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerConfig {
  customerId: number;
  customerName: string;
  generalConfig?: {
    rfidEnabled?: boolean;
    emailInputEnabled?: boolean;
    phoneNumberInputEnabled?: boolean;
    defaultShopperName?: Record<string, string>;
    multiLanguageSupportEnabled?: boolean;
    avatar?: string;
    logo?: string;
    customerEnum?: string;
    customerEnumId?: number;
    customerWideDevicePin?: string;
    rfidUrl?: string;
    enabledLanguages?: string[];
    v2DesignEnabled?: StoreEnablement;
    v2ProactiveRecommendationsEnabled?: StoreEnablement;
    v2LoggerEnabled?: StoreEnablement;
  };
  shopperApp?: {
    dressingRoomTimeoutSeconds?: number;
    defaultAvatarUrl?: string;
    logoUrl?: string;
    productDescriptionHeader?: Record<string, string>;
    greetingText?: Record<string, string>;
    bannerImageUrl?: string;
    bannerLinkUrl?: string;
    budgetEnabled?: boolean;
    priceEnabled?: boolean;
    scannerType?: string;
    displayTryThis?: boolean;
    proactiveRecommendationsEnabled?: boolean;
    inventoryQuantityEnabled?: boolean;
    requestTimerEnabled?: boolean;
    accessibilityImageUrl?: string;
    associateRequestEnabled?: StoreEnablement;
    checkoutRequest?: StoreEnablement;
  };
  assistApp?: {
    timeoutSeconds?: number;
    associateRequestEnabled?: StoreEnablement;
  };
  associateConfig?: {
    hardwareScanningEnabled?: boolean;
    insightsEnabled?: boolean;
    locatorType?: string;
    requestListType?: string;
  };
}

export interface StoreEnablement {
  enabledForAll: boolean;
  enabledOnStore: number[];
}

export interface CustomerResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface StoreGoal {
  upfvGoal: number;
  timeGoal: number;
  storeId: number;
}

interface StoreGoalResponse {
  data: StoreGoal;
  metadata: {
    code: number;
    message: string;
  };
}

export class CustomerService extends BaseApiService {
  private static instance: CustomerService;
  protected readonly coreService: CoreService;

  private constructor() {
    super();
    this.coreService = CoreService.getInstance();
  }

  public static getInstance(): CustomerService {
    if (!CustomerService.instance) {
      CustomerService.instance = new CustomerService();
    }
    return CustomerService.instance;
  }

  private getCoreBaseUrl(): string {
    return getApiConfig().core;
  }

  public async getAllCustomers({
    includeInactive = false,
  }: { includeInactive?: boolean } = {}): Promise<CustomerResponse<Customer[]>> {
    try {
      const response = await this.get<{ data: Customer[] }>(
        includeInactive ? "/customer?includeInactive=true" : "/customer",
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch customers",
      };
    }
  }

  public async getCustomerById(customerId: number): Promise<CustomerResponse<Customer>> {
    try {
      const response = await this.get<{ data: Customer }>(
        `/customer/${customerId}`,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch customer",
      };
    }
  }

  public async addCustomer(
    customerData: Omit<Customer, "id">,
  ): Promise<CustomerResponse<Customer>> {
    try {
      const response = await this.post<{ data: Customer }>(
        "/customer",
        customerData,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to create customer",
      };
    }
  }

  public async editCustomer(customerData: Customer): Promise<CustomerResponse<Customer>> {
    try {
      const { id, ...customerUpdates } = customerData;
      const response = await this.put<{ data: Customer }>(
        "/customer",
        { customerId: id, ...customerUpdates },
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to update customer",
      };
    }
  }

  public async getCustomerNoteById(noteId: number): Promise<CustomerResponse<CustomerNote>> {
    try {
      const response = await this.get<{ data: CustomerNote }>(
        `/customer/note/${noteId}`,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch customer note",
      };
    }
  }

  public async getAllCustomerNotes(customerId: number): Promise<CustomerResponse<CustomerNote[]>> {
    try {
      const response = await this.get<{ data: CustomerNote[] }>(
        `/customer/note/all/${customerId}`,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch customer notes",
      };
    }
  }

  public async addCustomerNote(
    noteData: Omit<CustomerNote, "id" | "createdAt" | "updatedAt">,
  ): Promise<CustomerResponse<CustomerNote>> {
    try {
      const response = await this.post<{ data: CustomerNote }>(
        "/customer/note",
        noteData,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to create customer note",
      };
    }
  }

  public async editCustomerNote(noteData: CustomerNote): Promise<CustomerResponse<CustomerNote>> {
    try {
      const { id, ...noteUpdates } = noteData;
      const response = await this.put<{ data: CustomerNote }>(
        `/customer/note/${id}`,
        noteUpdates,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to update customer note",
      };
    }
  }

  public async getAllCustomerConfigs(): Promise<CustomerResponse<CustomerConfig[]>> {
    try {
      const response = await this.get<{ data: CustomerConfig[] }>(
        "/v2/customer/config",
        this.getCoreBaseUrl(),
      );

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch customer configs",
      };
    }
  }

  public async getCustomerConfigByCustomerId(
    customerId: number,
  ): Promise<CustomerResponse<CustomerConfig>> {
    try {
      const response = await this.get<{ data: CustomerConfig }>(
        `/v2/customer/${customerId}/config`,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch customer config",
      };
    }
  }

  public async editCustomerConfig(
    customerId: number,
    configString: string,
  ): Promise<CustomerResponse<void>> {
    try {
      await this.put("/v2/customer/config", JSON.parse(configString), this.getCoreBaseUrl());
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to update customer config",
      };
    }
  }

  public async uploadFile(formData: FormData): Promise<CustomerResponse<string>> {
    try {
      const authHeader = this.coreService.getAuthorizationHeader();
      if (!authHeader) {
        return {
          success: false,
          error: "You must be logged in to access this resource",
        };
      }

      const token = authHeader.startsWith("Bearer ") ? authHeader : `Bearer ${authHeader}`;
      const config = getApiConfig();
      const response = await fetch(`${config.core}/upload`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: token,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `API request failed: ${response.status}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        return {
          success: false,
          error: errorMessage,
        };
      }

      const payload = await response.json();
      const uploadedUrl = payload?.data;
      if (typeof uploadedUrl !== "string") {
        return {
          success: false,
          error: "Failed to upload file",
        };
      }

      return {
        success: true,
        data: uploadedUrl,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to upload file",
      };
    }
  }

  public async getStoreGoal(storeId: number): Promise<CustomerResponse<StoreGoal>> {
    try {
      const response = await this.get<StoreGoalResponse>(
        `/store/goal/${storeId}`,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch store goal",
      };
    }
  }

  public async updateStoreGoal(
    storeId: number,
    goal: StoreGoal,
  ): Promise<CustomerResponse<StoreGoal>> {
    try {
      const response = await this.put<StoreGoalResponse>(
        `/store/goal`,
        goal,
        this.getCoreBaseUrl(),
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to update store goal",
      };
    }
  }
}
